import AdminHeader from "components/Headers/AdminHeader";
import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  UncontrolledTooltip,
  Input,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import Select, { components } from "react-select";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import {
  DEFAULT_RECORDS_PER_PAGE,
  fixedWidthCustomStyle,
} from "constants/CommonConstants";
import { STATUS_OPTIONS } from "components/constant";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSignatureList, deleteSignature, editSignature } from "action/signature";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { signatureList } from "constants/signature";

const Signatures = () => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);
  const signatureListData = useSelector(
    (state) => state.signature.signatureList,
  );
  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    searchSignatureName: "",
    searchDesignation: "",
    searchAuthorizedPersonName: "",
    status: null,
    filterParams: [],
  });
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [previewData, setPreviewData] = useState({
    src: "",
    width: 0,
    height: 0,
  });

  useEffect(() => {
    getSignatureList(dispatch, pageNum, filterState.filterParams);

    return () => {
      dispatch({ type: signatureList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (signatureListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: signatureListData?.data?.Signature?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (signatureListData?.code || signatureListData?.message) {
      setListState({
        isLoading: false,
        error:
          signatureListData?.message ||
          "Failed to load signature list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [signatureListData]);

  const tableData = signatureListData?.data?.Signature?.map((item, index) => {
    return {
      ...item,
      id: item.ID,
      srNo:
        (pageNum - 1) *
        (signatureListData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      name: item?.name || "-",
      authorizedPersonDesignation: item?.authorizedPersonDesignation || "-",
      authorizedPersonName: item?.authorizedPersonName || "-",
      status: item?.status || "-",
    };
  });

  const handleEditClick = (e, row) => {
    e.stopPropagation();
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/signatures/edit/${row.original.ID}`
        : `/admin/signatures/edit/${row.original.ID}`,
      {
        state: { mode: "edit" },
      },
    );
  };

  const handleAddSignatureClick = () => {
    navigate(
      role === "SUPER_ADMIN"
        ? "/super-admin/signatures/add-new"
        : "/admin/signatures/add-new",
    );
  };

  const handleDelete = (e, row) => {
    e.stopPropagation();
    showAlert({
      title: `Are you sure you want to delete the signature "${row.original.name}"?`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          const res = await deleteSignature(row.original.ID);
          if (res?.code === 200) {
            notify("success", res?.message || "Signature deleted successfully");
            getSignatureList(dispatch, pageNum, filterState.filterParams);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
              : res?.message || "Failed to delete signature";
            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Failed to delete signature");
        }
      },
    });
  };

  const toggleStatus = (signature) => {
    const { status, id, name } = signature;

    const isActive = status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";

    showAlert({
      title: `Are you sure you want to ${isActive ? "deactivate" : "activate"
        } this signature?`,
      message: `Signature "${name}" will be marked as ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          const signaturePayload = {
            status: newStatus,
          };
          const payloadFormData = new FormData();
          payloadFormData.append("signature", JSON.stringify(signaturePayload));

          const res = await editSignature(id, payloadFormData);
          if (res?.code === 200) {
            notify(
              "success",
              res?.message || `Status updated to ${newStatus.toLowerCase()}`,
            );
            getSignatureList(dispatch, pageNum, filterState.filterParams);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
              : res?.message || "Failed to update status!";
            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Failed to update status!");
        }
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchSignatureName?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filterState.searchSignatureName.trim()],
      });
    }

    if (filterState.searchDesignation?.trim()) {
      filterParams.push({
        field: "AuthorizedPersonDesignation",
        condition: "like",
        filterValues: [filterState.searchDesignation.trim()],
      });
    }

    if (filterState.searchAuthorizedPersonName?.trim()) {
      filterParams.push({
        field: "AuthorizedPersonName",
        condition: "like",
        filterValues: [filterState.searchAuthorizedPersonName.trim()],
      });
    }

    if (filterState.status?.value) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.status.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();
    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));
      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: true,
    }));
    setFilterState({
      searchSignatureName: "",
      searchDesignation: "",
      searchAuthorizedPersonName: "",
      status: null,
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  /* TABLE COLUMNS */
  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 70,
      centerAlign: true,
    },
    {
      Header: "Signature Name",
      accessor: "name",
      minWidth: 220,
      maxWidth: 280,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Designation",
      accessor: "authorizedPersonDesignation",
      minWidth: 180,
      maxWidth: 220,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Authorized Person Name",
      accessor: "authorizedPersonName",
      minWidth: 220,
      maxWidth: 280,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value || "-"}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      minWidth: 250,
      maxWidth: 300,
      Cell: ({ row }) => {
        const { ID, status, signatureFilePath } = row.original;
        const isActive = status === "ACTIVE";

        return (
          <div className="d-flex align-items-center gap-3">
            {signatureFilePath && (
              <>
                <Button
                  color="link"
                  className="btn-link d-flex align-items-center p-0"
                  id={`viewDoc${ID}`}
                  onClick={() =>
                    setPreviewData({
                      src: signatureFilePath,
                      width: 0,
                      height: 0,
                    })
                  }
                >
                  <i className="fa fa-file-alt mr-1" />
                </Button>
                <UncontrolledTooltip target={`viewDoc${ID}`}>
                  View Image
                </UncontrolledTooltip>
              </>
            )}

            <Button
              color="link"
              className="btn-link d-flex align-items-center p-0 table-action-edit"
              id={`edit${ID}`}
              onClick={(e) => handleEditClick(e, row)}
            >
              <i className="fas fa-edit text-primary" />
            </Button>
            <UncontrolledTooltip target={`edit${ID}`}>Edit</UncontrolledTooltip>

            <span className="text-muted">|</span>

            <Button
              color="link"
              size="lg"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${ID}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(row.original);
              }}
            >
              <i
                className={`fas status-toggle-icon ${isActive ? "fa-toggle-on text-success" : "fa-toggle-off text-warning"}`}
              />
            </Button>
            <UncontrolledTooltip target={`status${ID}`}>
              {isActive ? "Deactivate" : "Activate"}
            </UncontrolledTooltip>

            <Button
              color="link"
              className="btn-link d-flex align-items-center p-0 table-action-delete"
              id={`delete${ID}`}
              onClick={(e) => handleDelete(e, row)}
            >
              <i className="fas fa-trash text-danger" />
            </Button>
            <UncontrolledTooltip target={`delete${ID}`}>
              Delete
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <AdminHeader />

      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Signatures
                </CardTitle>

                <Button
                  className="btn-deep-blue"
                  onClick={handleAddSignatureClick}
                >
                  <i className="fa fa-plus mr-1" />
                  Signature
                </Button>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by signature name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchSignatureName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchSignatureName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by designation"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchDesignation}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchDesignation: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by authorized person"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchAuthorizedPersonName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchAuthorizedPersonName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      value={filterState.status}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          status: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""}`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {signatureListData?.data?.totalRecords > 0 ? (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing{" "}
                          {signatureListData?.data?.totalFilteredRecords} of{" "}
                          {signatureListData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {signatureListData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No signatures found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        signatureListData?.data?.totalFilteredRecords /
                        signatureListData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={!!previewData.src}
        toggle={() => setPreviewData({ src: "", width: 0, height: 0 })}
        size="xl"
        centered
      >
        <ModalHeader
          toggle={() => setPreviewData({ src: "", width: 0, height: 0 })}
        >
          Image Preview
        </ModalHeader>
        <ModalBody className="text-center">
          <img src={previewData.src} alt="Preview" className="preview-image" />
        </ModalBody>
      </Modal>
    </>
  );
};

export default Signatures;
